import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { notNull } from '@app/shared/utils';
import { deburr } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { Category } from '../../models';
import { TrailsService } from '../../services';

@Component({
  templateUrl: './sidenav-learning-studio.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SidenavLearningStudioComponent implements OnInit, OnDestroy {
  public readonly trailsService = inject(TrailsService);
  public countAllCourses: number;
  public allFilteredCategories = true;
  public search = new FormControl(undefined);
  public categories$ = new BehaviorSubject<Category[]>(undefined);
  public categoriesSelected$ = new BehaviorSubject<number[]>([]);
  public collectionsSelected$ = new BehaviorSubject<number[]>([]);
  public coursesSelected$ = new BehaviorSubject<number[]>([]);

  public ngOnInit(): void {
    this.countCourses();
    this.onSearch();
  }

  public ngOnDestroy(): void {
    this.categories$.complete();
    this.categoriesSelected$.complete();
    this.collectionsSelected$.complete();
    this.coursesSelected$.complete();
  }

  public onShowAll(): void {
    this.allFilteredCategories = true;
    const categories = [];
    const collections = [];
    const courses = [];
    this.categories$.value.forEach((category) => {
      categories.push(category.id);
      category.colecoes.forEach((collection) => {
        collections.push(collection.id);
        collection.cursos.forEach((course) => {
          courses.push(course.id);
        });
      });
    });
    this.categoriesSelected$.next(categories);
    this.collectionsSelected$.next(collections);
    this.coursesSelected$.next(courses);
    this.search.setValue(undefined, { emitEvent: false });
  }

  public onFilterCollection(id: number): void {
    if (this.allFilteredCategories) {
      this.allFilteredCategories = false;
      this.categoriesSelected$.next([id]);
    } else {
      let data = [];
      if (this.isSelected(id)) {
        data = this.categoriesSelected$.value.filter((category) => category !== id);
        if (!data?.length) {
          this.onShowAll();
        }
      } else {
        data = this.categoriesSelected$.value;
        data.push(id);
        this.search.setValue(null);
      }
      if (!!data?.length) {
        this.categoriesSelected$.next(data);
      }
    }
    this.validAllFilteredCategories();
  }

  public isSelected(id: number): boolean {
    return this.categoriesSelected$.value?.includes(id);
  }

  public get labelUcIvca(): string {
    if (this.trailsService.uc$.value) return 'menu.universidade-corporativa';
    if (this.trailsService.ivca$.value) return 'menu.ivca';
    return 'menu.trilhas-aprendizagem';
  }

  private onSearch(): void {
    this.search.valueChanges.subscribe((value: string) => this.searchCourseInCategory(value));
  }

  private searchCourseInCategory(title: string): void {
    if (title) {
      const categories = [];
      const collections = [];
      const courses = [];
      const deburredTitle = deburr(title).toLowerCase();

      this.categories$.value.forEach((category) => {
        if (deburr(category.nome).toLowerCase().includes(deburredTitle)) {
          categories.push(category.id);
          category.colecoes.forEach((collection) => {
            collections.push(collection.id);
            collection.cursos.forEach((course) => {
              courses.push(course.id);
            });
          });
        } else {
          category.colecoes.forEach((collection) => {
            if (deburr(collection.nome).toLowerCase().includes(deburredTitle)) {
              collections.push(collection.id);
              categories.push(category.id);
              collection.cursos.forEach((course) => {
                courses.push(course.id);
              });
            } else {
              collection.cursos.forEach((course) => {
                if (deburr(course.titulo).toLowerCase().includes(deburredTitle)) {
                  courses.push(course.id);
                  categories.push(category.id);
                  collections.push(collection.id);
                }
              });
            }
          });
        }
      });

      this.categoriesSelected$.next(categories);
      this.collectionsSelected$.next(collections);
      this.coursesSelected$.next(courses);
      this.validAllFilteredCategories();
    } else {
      this.onShowAll();
    }
  }

  private countCourses(): void {
    this.categories$.pipe(notNull()).subscribe((categories) => {
      this.countAllCourses = categories.reduce((accumulator, category) => accumulator + category.numberCourses, 0);
    });
  }

  private validAllFilteredCategories(): void {
    if (
      !this.categoriesSelected$.value.length ||
      this.categoriesSelected$.value.length === this.categories$.value?.length
    ) {
      this.allFilteredCategories = true;
    } else {
      this.allFilteredCategories = false;
    }
  }
}
